import React, { useState, useEffect } from 'react'
import { Select, Tag, message } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import 'antd/dist/reset.css'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import predefinedStudentColors from 'src/Common/predefinedStudentColors'
import 'src/Pages/LibraryPages/librarypages.scss'

const { Option } = Select

const ProjectAssignment = () => {
  const token = localStorage.getItem('token')
  const { groupId } = useParams()
  const [selectedProject, setSelectedProject] = useState(null)
  const [groupData, setGroupData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL_API}/InternshipProject/Mentor/AssignProject-Request?groupId=${groupId}`,
          {
            headers: {
              accept: '*/*',
              Authorization: `Bearer ${token}`,
            },
          },
        )
        setGroupData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error)
        message.error(error?.response?.data || 'Failed to load project assignment data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [groupId, token])

  const handleSubmit = async () => {
    if (!selectedProject) {
      message.warning('Please select a project first!')
      return
    }

    setSubmitting(true)

    const payload = {
      groupId: parseInt(groupId),
      projectId: selectedProject,
      membersStepsMap: groupData.membersStepsMap,
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL_API}/InternshipProject/Mentor/AssignProject`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      )

      message.success('Project assigned successfully!')
      navigate(`/group-detail/${groupId}`) // Redirect after success
    } catch (error) {
      console.error('Error assigning project:', error)
      message.error('Failed to assign project. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="lpg">
      <style>{`
        .ap-group { display:flex; align-items:baseline; gap:8px; flex-wrap:wrap;
          font-family:'Bricolage Grotesque',sans-serif; font-weight:800; letter-spacing:-.01em;
          color:#16233f; font-size:1.15rem; margin:0 0 16px; }
        .ap-group__name { color:#2563eb; }
        .ap-label { font-size:.82rem; font-weight:600; color:#3a4763; margin:0 0 8px; }
        .ap-pills { display:flex; gap:8px; flex-wrap:wrap; }
        .ap-pills .ant-tag { font-size:.78rem; font-weight:600; line-height:1.6;
          padding:.24rem .72rem; border-radius:999px; border:0; margin:0; }
        .ap-sec-title { font-family:'Bricolage Grotesque',sans-serif; font-weight:800;
          letter-spacing:-.01em; color:#16233f; font-size:1.05rem; margin:0 0 14px; }
        .ap-detail-card { margin:18px 0; }
        .ap-detail-card .ap-sec-title { margin-bottom:12px; }
        .ap-detail-row { margin:0 0 8px; color:#3a4763; font-size:.9rem; line-height:1.55; }
        .ap-detail-row:last-child { margin-bottom:0; }
        .ap-detail-row b { color:#16233f; font-weight:700; }
        .ap-fw-card { display:flex; flex-direction:column; gap:10px; }
        .ap-fw-card__title { font-family:'Bricolage Grotesque',sans-serif; font-weight:800;
          letter-spacing:-.01em; color:#16233f; font-size:1rem; margin:0; }
        .ap-fw-assigned { font-size:.82rem; font-weight:600; color:#3a4763; margin:0; }
        .ap-fw-assigned span { color:#2563eb; }
        .ap-tasks { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:8px; }
        .ap-tasks li { display:flex; align-items:flex-start; gap:8px; font-size:.86rem;
          color:#3a4763; line-height:1.5; }
        .ap-tasks li .anticon { color:#16a34a; margin-top:3px; flex:none; }
        .ap-submit { margin-top:22px; }
      `}</style>

      <div className="lpg__head">
        <div className="lpg__head-main">
          <span className="lpg__head-ic">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="7" width="18" height="13" rx="2" />
              <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18" />
            </svg>
          </span>
          <div>
            <h1 className="lpg__title">Assign Project</h1>
            <p className="lpg__sub">Assign an internship project.</p>
          </div>
        </div>
        <button
          type="button"
          className="lpg__back"
          onClick={() => navigate(`/group-detail/${groupId}`)}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M11 18l-6-6 6-6" />
          </svg>{' '}
          Back
        </button>
      </div>

      {loading ? (
        <div className="lpg__state">
          <div className="lpg__spin" />
          Loading...
        </div>
      ) : (
        <div className="lpg__panel-box">
          {groupData && (
            <>
              <h2 className="ap-group">
                Group: <span className="ap-group__name">{groupData.groupName}</span>
              </h2>

              <p className="ap-label">Members</p>

              <div className="ap-pills">
                {groupData.groupMembers?.map((member, index) => (
                  <Tag key={member.memberId} color={predefinedStudentColors(index)}>
                    {member.fullName || member.name}
                  </Tag>
                ))}
                <Tag color={predefinedStudentColors(4)}>Team</Tag>
              </div>
            </>
          )}

          <div className="lpg__field" style={{ marginTop: '18px' }}>
            <label className="lpg__label">Select a Project</label>
            <Select
              style={{ width: '100%' }}
              placeholder="Select a Project"
              size="large"
              onChange={(value) => setSelectedProject(value)}
            >
              {groupData?.availableProjects?.map((project) => (
                <Option key={project.id} value={project.id}>
                  {project.title}
                </Option>
              ))}
            </Select>
          </div>

          {selectedProject && (
            <>
              <div className="lpg__panel-box ap-detail-card">
                <h3 className="ap-sec-title">Project Details</h3>
                <p className="ap-detail-row">
                  <b>Title:</b>{' '}
                  {groupData.availableProjects.find((p) => p.id === selectedProject).title}
                </p>
                <p className="ap-detail-row">
                  <b>Description:</b>{' '}
                  {groupData.availableProjects.find((p) => p.id === selectedProject).description}
                </p>
              </div>

              <h3 className="ap-sec-title">Project Framework</h3>
              <div className="lpg__grid lpg__grid--wide">
                {groupData.frameworkSteps.map((step) => (
                  <div key={step.id} className="lpg__panel-box ap-fw-card">
                    <h4 className="ap-fw-card__title">{step.title}</h4>
                    <p className="ap-fw-assigned">
                      Assigned to:{' '}
                      <span>
                        {groupData.groupMembers?.find((member) => member.memberId === step.memberId)
                          ?.name || 'Team'}
                      </span>
                    </p>

                    <ul className="ap-tasks">
                      {step.tasks.map((task, index) => (
                        <li key={index}>
                          <CheckOutlined />
                          {task}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}
          {selectedProject && (
            <div className="ap-submit">
              <button
                type="button"
                className="lpg__btn-primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ProjectAssignment
